import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Toggle from "./Toggle";
import TranslatableField from "./TranslatableField";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { focusFirstError } from "../services/formFocus";
import { fetchCurrencies } from "../services/lookupService";
import { createCatalogueService, updateCatalogueService } from "../services/serviceService";
import { labelFor } from "../services/enumLabels";

const EMPTY = {
  name: { en: "", ar: "" },
  description: { en: "", ar: "" },
  categoryId: "",
  duration: "30",
  bufferBefore: "0",
  bufferAfter: "0",
  price: "",
  currencyId: "",
  // Tax included in the price unless the business says it is added on top.
  taxMode: "included",
  taxRate: "",
  isAvailableOnline: true,
  cancellationWindowHours: "",
  cancellationFeePercent: "0",
  cancellationPolicy: { en: "", ar: "" },
  isActive: true,
  sortOrder: "0",
};

/** An existing service -> the shape the form works with. */
const toForm = (service) =>
  service
    ? {
        name: service.translations?.name ?? { en: "", ar: "" },
        description: service.translations?.description ?? { en: "", ar: "" },
        categoryId: service.service_category_id ?? "",
        duration: String(service.duration ?? "30"),
        bufferBefore: String(service.buffer_before ?? "0"),
        bufferAfter: String(service.buffer_after ?? "0"),
        price: service.price ?? "",
        currencyId: service.currency?.id ?? "",
        taxMode: service.tax_mode ?? "included",
        taxRate: service.tax_rate ?? "",
        isAvailableOnline: Boolean(service.is_available_online),
        cancellationWindowHours: service.cancellation_window_hours ?? "",
        cancellationFeePercent: service.cancellation_fee_percent ?? "0",
        cancellationPolicy: service.translations?.cancellation_policy ?? { en: "", ar: "" },
        isActive: Boolean(service.is_active),
        sortOrder: String(service.sort_order ?? "0"),
      }
    : EMPTY;

/**
 * Create or edit one catalogue service (BIZ-05): what it is, how long it takes,
 * what it lists at, and how it may be booked. Which branches offer it is a
 * separate step, so this form stays about the service itself.
 */
export default function ServiceCatalogueModal({ service, categories = [], open, onClose, onSaved }) {
  const { t } = useTranslation();
  const [values, setValues] = useState(EMPTY);
  const [currencies, setCurrencies] = useState([]);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [saving, setSaving] = useState(false);

  const editing = Boolean(service);

  useEffect(() => {
    if (!open) return;

    setValues(toForm(service));
    setErrors({});
    setBanner("");
  }, [open, service]);

  useEffect(() => {
    if (!open) return;

    fetchCurrencies()
      .then(setCurrencies)
      .catch(() => setCurrencies([]));
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, saving]);

  const set = (field) => (e) => setValues({ ...values, [field]: e.target.value });
  const fieldError = (field) => errors[field]?.[0];

  /**
   * OUP-14 — API error key -> input id, in the order the fields are rendered,
   * so a failed save takes the user to the first one they can see.
   */
  const FIELD_IDS = {
    name: "name-en",
    description: "description-en",
    service_category_id: "service-category",
    duration: "service-duration",
    buffer_before: "service-bufferBefore",
    buffer_after: "service-bufferAfter",
    price: "service-price",
    currency_id: "service-currency",
    tax_mode: "service-tax",
    tax_rate: "service-taxRate",
    cancellation_window_hours: "service-cancellationWindowHours",
    cancellation_fee_percent: "service-cancellationFeePercent",
    cancellation_policy: "cancellation_policy-en",
    sort_order: "service-sortOrder",
  };

  const report = (found) => {
    setErrors(found);
    focusFirstError(found, FIELD_IDS);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBanner("");

    // OUP-14 — the API refuses all three now; say so here rather than spending
    // a round trip to be told.
    const found = {};
    if (String(values.price ?? "").trim() === "") found.price = [t("catalogue.form.errors.price")];
    if (!values.currencyId) found.currency_id = [t("catalogue.form.errors.currency")];
    if (values.taxMode === "on_top") {
      const rate = Number(values.taxRate);
      if (String(values.taxRate ?? "").trim() === "") found.tax_rate = [t("catalogue.form.errors.taxRateRequired")];
      else if (!(rate > 0 && rate <= 100) || !/^\d+(\.\d{1,2})?$/.test(String(values.taxRate).trim())) {
        found.tax_rate = [t("catalogue.form.errors.taxRateRange")];
      }
    }

    if (Object.keys(found).length > 0) {
      report(found);
      setBanner(t("catalogue.form.errors.summary"));
      return;
    }

    setSaving(true);
    setErrors({});

    try {
      const saved = editing
        ? await updateCatalogueService(service.id, values)
        : await createCatalogueService(values);

      onSaved(saved, editing);
    } catch (err) {
      report(apiFieldErrors(err));
      setBanner(apiErrorMessage(err, t("catalogue.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const number = (field, label, { hint, min = "0", step = "1" } = {}) => (
    <div>
      <label className="field-label" htmlFor={`service-${field}`}>
        {label}
      </label>
      <input
        id={`service-${field}`}
        type="number"
        min={min}
        step={step}
        dir="ltr"
        className="field-input"
        value={values[field]}
        onChange={set(field)}
      />
      {hint && <p className="mt-1 text-xs text-ink-2">{hint}</p>}
      {fieldError(field) && <p className="field-error">{fieldError(field)}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
      <div role="dialog" aria-modal="true" className="w-full max-w-3xl rounded-xl bg-surface shadow-xl">
        <form onSubmit={submit} className="flex max-h-[85vh] flex-col" noValidate>
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <h2 className="text-lg font-semibold">
              {editing ? t("catalogue.form.editTitle") : t("catalogue.form.addTitle")}
            </h2>
            <button type="button" onClick={onClose} className="text-sm text-ink-2 hover:text-ink">
              {t("common.close")}
            </button>
          </div>

          <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
            {banner && (
              <div className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
                {banner}
              </div>
            )}

            <TranslatableField
              name="name"
              label={t("catalogue.form.name")}
              value={values.name}
              onChange={(name) => setValues({ ...values, name })}
              errors={errors}
              required
            />

            <TranslatableField
              name="description"
              label={t("catalogue.form.description")}
              value={values.description}
              onChange={(description) => setValues({ ...values, description })}
              errors={errors}
              textarea
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="field-label" htmlFor="service-category">
                  {t("catalogue.form.category")}
                </label>
                <select
                  id="service-category"
                  className="field-input"
                  value={values.categoryId}
                  onChange={set("categoryId")}
                >
                  <option value="">{t("common.none")}</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.parent ? `${category.parent.name} › ` : ""}
                      {category.name}
                    </option>
                  ))}
                </select>
                {fieldError("service_category_id") && (
                  <p className="field-error">{fieldError("service_category_id")}</p>
                )}
              </div>

              {number("sortOrder", t("catalogue.form.sortOrder"))}
            </div>

            {/* --- Timing: the slot a booking occupies --- */}
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-2">
                {t("catalogue.form.timing")}
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                {number("duration", `${t("catalogue.form.duration")} *`, { min: "1" })}
                {number("bufferBefore", t("catalogue.form.bufferBefore"))}
                {number("bufferAfter", t("catalogue.form.bufferAfter"))}
              </div>
            </div>

            {/* --- Price and tax --- */}
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-2">
                {t("catalogue.form.pricing")}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {number("price", `${t("catalogue.form.price")} *`, {
                  step: "0.01",
                  hint: t("catalogue.form.priceHint"),
                })}

                <div>
                  <label className="field-label" htmlFor="service-currency">
                    {t("catalogue.form.currency")} <span className="text-danger-text">*</span>
                  </label>
                  <select
                    id="service-currency"
                    className="field-input"
                    value={values.currencyId}
                    onChange={set("currencyId")}
                  >
                    <option value="">{t("common.none")}</option>
                    {currencies.map((currency) => (
                      <option key={currency.id} value={currency.id}>
                        {currency.code} — {currency.name}
                      </option>
                    ))}
                  </select>
                  {fieldError("currency_id") && (
                    <p className="field-error">{fieldError("currency_id")}</p>
                  )}
                </div>

                <div>
                  <label className="field-label" htmlFor="service-tax">
                    {t("catalogue.form.taxMode")}
                  </label>
                  <select
                    id="service-tax"
                    className="field-input"
                    value={values.taxMode}
                    onChange={set("taxMode")}
                  >
                    {["included", "on_top"].map((mode) => (
                      <option key={mode} value={mode}>
                        {labelFor("tax_mode", mode)}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-ink-2">{t(`catalogue.form.taxHints.${values.taxMode}`)}</p>
                  {fieldError("tax_mode") && <p className="field-error">{fieldError("tax_mode")}</p>}
                </div>

                {/* Only when tax is added on top; an included price carries no rate. */}
                {values.taxMode === "on_top" && (
                  <div>
                    <label className="field-label" htmlFor="service-taxRate">
                      {t("catalogue.form.taxRate")} <span className="text-danger-text">*</span>
                    </label>
                    <input
                      id="service-taxRate"
                      type="number"
                      min="0.01"
                      max="100"
                      step="0.01"
                      dir="ltr"
                      className="field-input"
                      value={values.taxRate}
                      onChange={set("taxRate")}
                    />
                    {fieldError("tax_rate") && <p className="field-error">{fieldError("tax_rate")}</p>}
                  </div>
                )}
              </div>
            </div>

            {/* --- Cancellation --- */}
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-2">
                {t("catalogue.form.cancellation")}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {number("cancellationWindowHours", t("catalogue.form.cancellationWindow"), {
                  hint: t("catalogue.form.cancellationWindowHint"),
                })}
                {number("cancellationFeePercent", t("catalogue.form.cancellationFee"), {
                  step: "0.01",
                })}
              </div>
              <div className="mt-3">
                <TranslatableField
                  name="cancellation_policy"
                  label={t("catalogue.form.cancellationNote")}
                  value={values.cancellationPolicy}
                  onChange={(cancellationPolicy) => setValues({ ...values, cancellationPolicy })}
                  errors={errors}
                  textarea
                  rows={2}
                />
              </div>
            </div>

            <Toggle
              label={t("catalogue.form.online")}
              hint={t("catalogue.form.onlineHint")}
              checked={values.isAvailableOnline}
              onChange={(checked) => setValues({ ...values, isAvailableOnline: checked })}
            />

            <Toggle
              label={t("catalogue.form.active")}
              hint={t("catalogue.form.activeHint")}
              checked={values.isActive}
              onChange={(checked) => setValues({ ...values, isActive: checked })}
            />
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? t("common.loading") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
